import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Home, FileText, Scale, Shield, Euro, AlertTriangle, Users, Globe } from 'lucide-react';

export default function TermsPage() {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-slate-100 pt-20" data-testid="terms-page">
      <div className="max-w-4xl mx-auto px-4 py-12">

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-military-gold/10 border border-military-gold/30 rounded-full mb-4">
            <Scale className="w-4 h-4 text-military-gold" />
            <span className="text-military-gold text-sm">{t('legal.agb', 'Allgemeine Geschäftsbedingungen (AGB)')}</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-800 mb-2">
            {t('legal.terms_title', 'TERMS OF SERVICE')}
          </h1>
          <p className="text-slate-600">
            {t('legal.last_updated', 'Last updated')}: {t('legal.date_jan_2026', 'January 13, 2026')} | {t('legal.effective', 'Effective')}: {t('legal.date_jan_2026', 'January 13, 2026')}
          </p>
        </div>

        {/* Draft Notice */}
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-amber-800 font-semibold text-sm">{t('legal.draft_notice_title', 'DRAFT — REQUIRES LEGAL REVIEW')}</p>
            <p className="text-amber-700 text-sm mt-1">{t('legal.draft_notice_body', 'This document is under legal review and does not constitute final legal advice or binding terms.')}</p>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 space-y-8">

          {/* Section 1 */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-signal-blue" />
              {t('terms.s1.title', '1. Company Information & Legal Identity')}
            </h2>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-slate-700 text-sm space-y-2">
              <p><strong>{t('terms.s1.box.l.provider', 'Service Provider')}:</strong> {t('terms.s1.box.v.provider', 'TLA Housing Europe')}</p>
              <p><strong>{t('terms.s1.box.l.address', 'Registered Address')}:</strong> {t('terms.s1.box.v.address', '[Company Address, Germany]')}</p>
              <p><strong>{t('terms.s1.box.l.register', 'Commercial Register')}:</strong> {t('terms.s1.box.v.register', '[Handelsregister Number]')}</p>
              <p><strong>{t('terms.s1.box.l.vat', 'VAT ID')}:</strong> {t('terms.s1.box.v.vat', '[USt-IdNr.]')}</p>
              <p><strong>{t('terms.s1.box.l.director', 'Managing Director')}:</strong> {t('terms.s1.box.v.director', '[Name]')}</p>
              <p><strong>{t('terms.s1.box.l.contact', 'Contact')}:</strong> legal@tlahousing.eu</p>
              <p><strong>{t('terms.s1.box.l.authority', 'Supervisory Authority')}:</strong> {t('terms.s1.box.v.authority', '[Applicable German Authority]')}</p>
            </div>
            <p className="text-slate-600 text-sm mt-3 italic">{t('terms.s1.note', 'Note: Platform operator information must be completed before commercial operations commence.')}</p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">{t('terms.s2.title', '2. Scope of Application & Definitions')}</h2>
            <p className="text-slate-600 leading-relaxed mb-4">{t('terms.s2.p1', 'These General Terms and Conditions ("Terms" or "AGB") govern the use of the TLA Housing Europe platform ("Platform", "Service", "we", "us") and all related services. These Terms apply to:')}</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li><span className="font-semibold">{t('terms.s2.li1.l', 'Guests/Users')}:</span> {t('terms.s2.li1.d', 'Individuals seeking temporary housing accommodations')}</li>
              <li><span className="font-semibold">{t('terms.s2.li2.l', 'Property Owners/Hosts')}:</span> {t('terms.s2.li2.d', 'Individuals or entities listing properties on our Platform')}</li>
              <li><span className="font-semibold">{t('terms.s2.li3.l', 'All Users')}:</span> {t('terms.s2.li3.d', 'Anyone accessing or using the Platform')}</li>
            </ul>
            <p className="text-slate-600 leading-relaxed">{t('terms.s2.p2', 'Important: TLA Housing Europe operates as a marketplace platform connecting property owners with potential guests. We are not a party to any rental agreement between hosts and guests. The rental contract is concluded directly between the property owner and the guest.')}</p>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">{t('terms.s3.title', '3. Platform Services')}</h2>
            <p className="text-slate-600 leading-relaxed mb-4">{t('terms.s3.p1', 'TLA Housing Europe provides the following services:')}</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li>{t('terms.s3.li1', 'Property listing and search functionality for TLA-compliant accommodations')}</li>
              <li>{t('terms.s3.li2', 'Communication tools between hosts and guests')}</li>
              <li>{t('terms.s3.li3', 'Booking inquiry and calendar management systems')}</li>
              <li>{t('terms.s3.li4', 'Secure payment processing for deposits via Stripe')}</li>
              <li>{t('terms.s3.li5', 'AI-powered location assistance and recommendations')}</li>
              <li>{t('terms.s3.li6', 'Subscription services for property owners')}</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-signal-blue" />
              {t('terms.s4.title', '4. User Registration & Account Responsibilities')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">{t('terms.s4.p1', 'To access certain features, you must create an account. By registering, you confirm that:')}</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li>{t('terms.s4.li1', 'You are at least 18 years of age')}</li>
              <li>{t('terms.s4.li2', 'All information provided is accurate, complete, and current')}</li>
              <li>{t('terms.s4.li3', 'You have legal capacity to enter into binding agreements')}</li>
              <li>{t('terms.s4.li4', 'You will maintain the confidentiality of your login credentials')}</li>
              <li>{t('terms.s4.li5', 'You accept full responsibility for all activities under your account')}</li>
            </ul>
            <p className="text-slate-600 leading-relaxed">{t('terms.s4.p2', 'We reserve the right to suspend or terminate accounts that violate these Terms, contain false information, or are used for fraudulent purposes.')}</p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">{t('terms.s5.title', '5. Booking Process & Contract Formation')}</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              <strong>{t('terms.s5.h1', '5.1 Booking Inquiry')}:</strong> {t('terms.s5.p1', 'When you submit a booking inquiry, you are making a request to the property owner. This does not constitute a binding contract.')}
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">
              <strong>{t('terms.s5.h2', '5.2 Approval')}:</strong> {t('terms.s5.p2', 'The property owner may approve or decline your inquiry at their discretion. Approval of the inquiry is an offer to enter into a rental agreement.')}
            </p>
            <p className="text-slate-600 leading-relaxed mb-3">
              <strong>{t('terms.s5.h3', '5.3 Contract Formation')}:</strong> {t('terms.s5.p3', 'A binding rental contract is formed when:')}
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li>{t('terms.s5.li1', 'The property owner approves your booking inquiry')}</li>
              <li>{t('terms.s5.li2', 'You pay the required deposit through our secure payment system')}</li>
              <li>{t('terms.s5.li3', 'You receive confirmation of your booking')}</li>
            </ul>
            <p className="text-slate-600 leading-relaxed">
              <strong>{t('terms.s5.h4', '5.4 Direct Contract')}:</strong> {t('terms.s5.p4', 'The rental agreement is between you and the property owner. TLA Housing Europe facilitates the booking but is not a party to the rental contract.')}
            </p>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Euro className="w-5 h-5 text-military-gold" />
              {t('terms.s6.title', '6. Payments, Deposits & Fees')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              <strong>{t('terms.s6.h1', '6.1 Deposit Requirements')}:</strong> {t('terms.s6.p1', 'Upon booking approval, guests must pay a deposit of 25% of the estimated booking value (minimum €100) to confirm the reservation.')}
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">
              <strong>{t('terms.s6.h2', '6.2 Payment Processing')}:</strong> {t('terms.s6.p2', 'All payments are processed securely through Stripe, a PCI-DSS compliant payment processor. We do not store your full credit card details.')}
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">
              <strong>{t('terms.s6.h3', '6.3 Platform Service Fees')}:</strong> {t('terms.s6.p3', 'Property owners pay a monthly subscription fee for listing services. Guests are not charged platform fees for using our search and booking services.')}
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">
              <strong>{t('terms.s6.h4', '6.4 Currency')}:</strong> {t('terms.s6.p4', 'All prices are displayed and charged in Euros (EUR) unless otherwise stated.')}
            </p>
            <p className="text-slate-600 leading-relaxed">
              <strong>{t('terms.s6.h5', '6.5 Remaining Balance')}:</strong> {t('terms.s6.p5', 'The remaining balance (after deposit) is typically paid directly to the property owner according to the terms agreed between guest and host.')}
            </p>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              {t('terms.s7.title', '7. Cancellation Policy & Refund Terms')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              <strong>{t('terms.s7.h1', '7.1 Guest Cancellation Rights')}:</strong> {t('terms.s7.p1', 'Guests may cancel confirmed bookings subject to the following refund schedule based on the time remaining before check-in:')}
            </p>
            <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
              <table className="w-full text-slate-600 text-sm">
                <thead>
                  <tr className="border-b border-slate-300">
                    <th className="text-left py-2 text-slate-700">{t('terms.s7.tbl.col1', 'Cancellation Timing')}</th>
                    <th className="text-left py-2 text-slate-700">{t('terms.s7.tbl.col2', 'Refund Amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-200">
                    <td className="py-2">{t('terms.s7.tbl.r1.timing', '7+ days before check-in')}</td>
                    <td className="py-2 text-emerald-600">{t('terms.s7.tbl.r1.refund', 'Full refund (minus processing fees)')}</td>
                  </tr>
                  <tr className="border-b border-slate-200">
                    <td className="py-2">{t('terms.s7.tbl.r2.timing', '3–7 days before check-in')}</td>
                    <td className="py-2 text-amber-600">{t('terms.s7.tbl.r2.refund', '50% refund')}</td>
                  </tr>
                  <tr className="border-b border-slate-200">
                    <td className="py-2">{t('terms.s7.tbl.r3.timing', '1–3 days before check-in')}</td>
                    <td className="py-2 text-orange-600">{t('terms.s7.tbl.r3.refund', '25% refund')}</td>
                  </tr>
                  <tr>
                    <td className="py-2">{t('terms.s7.tbl.r4.timing', 'Less than 24 hours before check-in')}</td>
                    <td className="py-2 text-red-600">{t('terms.s7.tbl.r4.refund', 'No refund')}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-slate-600 leading-relaxed mb-3">
              <strong>{t('terms.s7.h2', '7.2 Processing Fees')}:</strong> {t('terms.s7.p2', 'All refunds are subject to the following non-refundable fees:')}
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-1 mb-4 text-sm">
              <li>{t('terms.s7.fee.li1', 'Platform processing fee: Percentage of deposit (configured by administrator)')}</li>
              <li>{t('terms.s7.fee.li2', 'Property owner cancellation fee: Percentage of deposit (configured by administrator)')}</li>
              <li>{t('terms.s7.fee.li3', 'Minimum fees may apply regardless of deposit amount')}</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-4">
              <strong>{t('terms.s7.h3', '7.3 Host Cancellation')}:</strong> {t('terms.s7.p3', 'If a property owner cancels a confirmed booking, the guest receives a full refund of all payments made through the Platform.')}
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">
              <strong>{t('terms.s7.h4', '7.4 Refund Processing Time')}:</strong> {t('terms.s7.p4', 'Refunds are processed within 5–10 business days. The actual credit to your payment method may take additional time depending on your bank or card issuer.')}
            </p>
            <p className="text-slate-600 leading-relaxed">
              <strong>{t('terms.s7.h5', '7.5 Exceptional Circumstances')}:</strong> {t('terms.s7.p5', 'We may, at our sole discretion, provide full or partial refunds in cases of documented emergencies, natural disasters, or other extraordinary circumstances.')}
            </p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-signal-blue" />
              {t('terms.s8.title', '8. EU Consumer Rights & Right of Withdrawal')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              <strong>{t('terms.s8.h1', '8.1 Right of Withdrawal (Widerrufsrecht)')}:</strong> {t('terms.s8.p1', 'Under EU Consumer Rights Directive 2011/83/EU, consumers have a 14-day right to withdraw from distance contracts without giving any reason.')}
            </p>
            <p className="text-slate-600 leading-relaxed mb-3">
              <strong>{t('terms.s8.h2', '8.2 Exception for Accommodation Services')}:</strong> {t('terms.s8.p2', 'Pursuant to Article 16(l) of the Consumer Rights Directive, the right of withdrawal does not apply to contracts for accommodation services where a specific date or period of performance is provided. This means:')}
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
              <li>{t('terms.s8.li1', 'Booking confirmations with specific check-in dates are exempt from the 14-day withdrawal right')}</li>
              <li>{t('terms.s8.li2', 'Cancellation is governed by our Cancellation Policy (Section 7)')}</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-4">
              <strong>{t('terms.s8.h3', '8.3 Subscription Services')}:</strong> {t('terms.s8.p3', 'For property owner subscriptions (which are not accommodation services), you have the right to withdraw within 14 days of purchase. To exercise this right, contact us at legal@tlahousing.eu with your withdrawal request.')}
            </p>
            <p className="text-slate-600 leading-relaxed">
              <strong>{t('terms.s8.h4', '8.4 Model Withdrawal Form')}:</strong> {t('terms.s8.p4', 'A model withdrawal form is available upon request and can be sent to legal@tlahousing.eu.')}
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">{t('terms.s9.title', '9. Property Listings & Host Obligations')}</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              <strong>{t('terms.s9.h1', '9.1 Accuracy')}:</strong> {t('terms.s9.p1', 'Property owners must ensure all listing information is accurate, complete, and not misleading. This includes descriptions, photos, pricing, availability, and amenities.')}
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">
              <strong>{t('terms.s9.h2', '9.2 TLA Compliance')}:</strong> {t('terms.s9.p2', 'Properties listed as "TLA-eligible" must meet the applicable requirements. However, TLA Housing Europe does not guarantee TLA approval, which is subject to verification by the guest\'s housing office.')}
            </p>
            <p className="text-slate-600 leading-relaxed mb-3">
              <strong>{t('terms.s9.h3', '9.3 Legal Compliance')}:</strong> {t('terms.s9.p3', 'Property owners must comply with all applicable laws, including:')}
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-1 mb-4">
              <li>{t('terms.s9.li1', 'Local rental regulations and permits')}</li>
              <li>{t('terms.s9.li2', 'Tax obligations (income tax, VAT if applicable, tourist taxes)')}</li>
              <li>{t('terms.s9.li3', 'Safety and building code requirements')}</li>
              <li>{t('terms.s9.li4', 'Insurance requirements')}</li>
              <li>{t('terms.s9.li5', 'Data protection regulations')}</li>
            </ul>
            <p className="text-slate-600 leading-relaxed">
              <strong>{t('terms.s9.h4', '9.4 Platform Rights')}:</strong> {t('terms.s9.p4', 'We reserve the right to remove listings that violate these Terms, contain false information, or receive multiple verified complaints.')}
            </p>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">{t('terms.s10.title', '10. Platform Liability & Disclaimers')}</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              <strong>{t('terms.s10.h1', '10.1 Marketplace Role')}:</strong> {t('terms.s10.p1', 'TLA Housing Europe operates as an online marketplace. We are not a real estate agent, property manager, or party to rental agreements. We do not own, manage, or control any listed properties.')}
            </p>
            <p className="text-slate-600 leading-relaxed mb-3">
              <strong>{t('terms.s10.h2', '10.2 No Guarantee')}:</strong> {t('terms.s10.p2', 'We do not guarantee:')}
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-1 mb-4">
              <li>{t('terms.s10.li1', 'The accuracy of listing information provided by hosts')}</li>
              <li>{t('terms.s10.li2', 'The quality, safety, or legality of listed properties')}</li>
              <li>{t('terms.s10.li3', 'TLA eligibility or reimbursement approval')}</li>
              <li>{t('terms.s10.li4', 'The conduct of users on or off the Platform')}</li>
              <li>{t('terms.s10.li5', 'Continuous, uninterrupted availability of the Platform')}</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-4">
              <strong>{t('terms.s10.h3', '10.3 Limitation of Liability')}:</strong> {t('terms.s10.p3', 'To the maximum extent permitted by applicable law, TLA Housing Europe\'s total liability shall not exceed the greater of (a) the amount you paid us in the 12 months prior to the claim, or (b) €100.')}
            </p>
            <p className="text-slate-600 leading-relaxed">
              <strong>{t('terms.s10.h4', '10.4 Mandatory Rights')}:</strong> {t('terms.s10.p4', 'Nothing in these Terms limits your statutory rights as a consumer under EU law, including rights under the Consumer Rights Directive and unfair contract terms regulations.')}
            </p>
          </section>

          {/* Section 11 */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">{t('terms.s11.title', '11. Prohibited Conduct')}</h2>
            <p className="text-slate-600 leading-relaxed mb-4">{t('terms.s11.p1', 'You agree not to:')}</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li>{t('terms.s11.li1', 'Post false, misleading, or fraudulent information')}</li>
              <li>{t('terms.s11.li2', 'Circumvent the Platform to avoid fees or facilitate payments outside the system')}</li>
              <li>{t('terms.s11.li3', 'Use the Platform for money laundering or other illegal financial activities')}</li>
              <li>{t('terms.s11.li4', 'Discriminate against users based on protected characteristics')}</li>
              <li>{t('terms.s11.li5', 'Harass, threaten, or abuse other users')}</li>
              <li>{t('terms.s11.li6', 'Infringe on intellectual property rights')}</li>
              <li>{t('terms.s11.li7', 'Attempt to gain unauthorized access to our systems')}</li>
              <li>{t('terms.s11.li8', 'Use automated tools to scrape or harvest data')}</li>
              <li>{t('terms.s11.li9', 'Interfere with the proper operation of the Platform')}</li>
            </ul>
          </section>

          {/* Section 12 */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">{t('terms.s12.title', '12. Dispute Resolution & Applicable Law')}</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              <strong>{t('terms.s12.h1', '12.1 Applicable Law')}:</strong> {t('terms.s12.p1', 'These Terms are governed by the laws of the Federal Republic of Germany, without regard to conflict of law principles. For consumers, mandatory consumer protection laws of your country of residence shall also apply.')}
            </p>
            <p className="text-slate-600 leading-relaxed mb-3">
              <strong>{t('terms.s12.h2', '12.2 Jurisdiction')}:</strong> {t('terms.s12.p2', 'For disputes arising from these Terms:')}
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-1 mb-4">
              <li>{t('terms.s12.li1', 'Consumers may bring claims in their country of residence or Germany')}</li>
              <li>{t('terms.s12.li2', 'Business users are subject to the exclusive jurisdiction of German courts')}</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-4">
              <strong>{t('terms.s12.h3', '12.3 EU Online Dispute Resolution')}:</strong> {t('terms.s12.p3', 'The European Commission provides an Online Dispute Resolution (ODR) platform for consumers:')}{' '}
              <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-signal-blue hover:underline">https://ec.europa.eu/consumers/odr</a>
            </p>
            <p className="text-slate-600 leading-relaxed">
              <strong>{t('terms.s12.h4', '12.4 Contact for Disputes')}:</strong> {t('terms.s12.p4', 'Before initiating formal proceedings, we encourage you to contact us at legal@tlahousing.eu to attempt an amicable resolution.')}
            </p>
          </section>

          {/* Section 13 */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">{t('terms.s13.title', '13. Data Protection')}</h2>
            <p className="text-slate-600 leading-relaxed">
              {t('terms.s13.p1', 'The collection and processing of personal data is governed by our')}{' '}
              <Link to="/privacy" className="text-signal-blue hover:underline">{t('footer.privacy', 'Privacy Policy')}</Link>,{' '}
              {t('terms.s13.p2', 'which forms an integral part of these Terms. By using the Platform, you consent to the data practices described in our Privacy Policy in accordance with the General Data Protection Regulation (GDPR).')}
            </p>
          </section>

          {/* Section 14 */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">{t('terms.s14.title', '14. Modifications to Terms')}</h2>
            <p className="text-slate-600 leading-relaxed mb-4">{t('terms.s14.p1', 'We may modify these Terms at any time. For significant changes:')}</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li>{t('terms.s14.li1', 'We will provide at least 30 days notice before changes take effect')}</li>
              <li>{t('terms.s14.li2', 'Notice will be sent via email and/or displayed on the Platform')}</li>
              <li>{t('terms.s14.li3', 'Continued use after the effective date constitutes acceptance')}</li>
              <li>{t('terms.s14.li4', 'If you do not agree to the changes, you may terminate your account')}</li>
            </ul>
          </section>

          {/* Section 15 */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">{t('terms.s15.title', '15. Severability')}</h2>
            <p className="text-slate-600 leading-relaxed">{t('terms.s15.p1', 'If any provision of these Terms is found to be invalid or unenforceable, that provision shall be modified to the minimum extent necessary to make it valid and enforceable, or if modification is not possible, shall be severed from these Terms. The remaining provisions shall continue in full force and effect.')}</p>
          </section>

          {/* Section 16 */}
          <section>
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">{t('terms.s16.title', '16. Contact Information')}</h2>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-slate-700">
              <p className="mb-2"><strong>{t('terms.s16.box.l.general', 'General Inquiries')}:</strong> support@tlahousing.eu</p>
              <p className="mb-2"><strong>{t('terms.s16.box.l.legal', 'Legal Matters')}:</strong> legal@tlahousing.eu</p>
              <p className="mb-2"><strong>{t('terms.s16.box.l.privacy', 'Data Protection')}:</strong> privacy@tlahousing.eu</p>
              <p><strong>{t('terms.s16.box.l.complaints', 'Complaints')}:</strong> complaints@tlahousing.eu</p>
            </div>
          </section>

        </div>

        {/* Version History */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mt-8">
          <h3 className="font-heading text-lg font-bold text-slate-800 mb-3">{t('legal.version_history', 'Version History')}</h3>
          <div className="text-slate-600 text-sm space-y-1">
            <p>• {t('terms.ver.v2', 'Version 2.0 - January 13, 2026: Added booking deposit and refund fee terms, EU compliance updates')}</p>
            <p>• {t('terms.ver.v1', 'Version 1.0 - January 2026: Initial terms of service')}</p>
          </div>
        </div>

        {/* Back Link */}
        <div className="text-center mt-8">
          <Link to="/" className="text-signal-blue hover:text-signal-blue-hover inline-flex items-center gap-2">
            <Home className="w-4 h-4" />
            {t('legal.back_home', 'Back to Home')}
          </Link>
        </div>

      </div>
    </div>
  );
}
